import type { TypedSupabaseClient } from '../client';
import type {
  IFormationPageRepository,
  FormationPage,
  CreateFormationPageInput,
  UpdateFormationPageInput,
} from '../../../interfaces/src';

export class SupabaseFormationPageRepository implements IFormationPageRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<FormationPage[]> {
    const { data, error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .select('*')
      .order('display_order')
      .order('created_at');
    if (error) throw error;
    return (data ?? []).map(mapFormationPage);
  }

  async listPublished(): Promise<FormationPage[]> {
    const { data, error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .select('*')
      .eq('is_published', true)
      .order('display_order')
      .order('created_at');
    if (error) throw error;
    return (data ?? []).map(mapFormationPage);
  }

  async getById(id: string): Promise<FormationPage | null> {
    const { data, error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapFormationPage(data) : null;
  }

  async getBySlug(slug: string): Promise<FormationPage | null> {
    const { data, error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return data ? mapFormationPage(data) : null;
  }

  async create(input: CreateFormationPageInput): Promise<FormationPage> {
    const { data, error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .insert(mapFormationPagePatch(input))
      .select()
      .single();
    if (error) throw error;
    return mapFormationPage(data);
  }

  async update(id: string, input: UpdateFormationPageInput): Promise<FormationPage> {
    const { data, error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .update(mapFormationPagePatch(input))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapFormationPage(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (this.supabase as unknown as { from: (t: string) => any })
      .from('formation_pages')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await (this.supabase as unknown as { from: (t: string) => any })
        .from('formation_pages')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }
}

function mapFormationPagePatch(
  input: CreateFormationPageInput | UpdateFormationPageInput,
): Record<string, unknown> {
  const map: Record<string, string> = {
    slug: 'slug',
    titleFr: 'title_fr',
    titleAr: 'title_ar',
    titleEn: 'title_en',
    descriptionFr: 'description_fr',
    descriptionAr: 'description_ar',
    descriptionEn: 'description_en',
    contentFr: 'content_fr',
    contentAr: 'content_ar',
    contentEn: 'content_en',
    categoryFr: 'category_fr',
    categoryAr: 'category_ar',
    categoryEn: 'category_en',
    icon: 'icon',
    isPublished: 'is_published',
    displayOrder: 'display_order',
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) patch[col] = v;
  }
  return patch;
}

function mapFormationPage(row: Record<string, unknown>): FormationPage {
  return {
    id: row['id'] as string,
    slug: row['slug'] as string,
    titleFr: (row['title_fr'] as string) ?? null,
    titleAr: (row['title_ar'] as string) ?? null,
    titleEn: (row['title_en'] as string) ?? null,
    descriptionFr: (row['description_fr'] as string) ?? null,
    descriptionAr: (row['description_ar'] as string) ?? null,
    descriptionEn: (row['description_en'] as string) ?? null,
    contentFr: (row['content_fr'] as string) ?? null,
    contentAr: (row['content_ar'] as string) ?? null,
    contentEn: (row['content_en'] as string) ?? null,
    categoryFr: (row['category_fr'] as string) ?? null,
    categoryAr: (row['category_ar'] as string) ?? null,
    categoryEn: (row['category_en'] as string) ?? null,
    icon: (row['icon'] as string) ?? null,
    isPublished: (row['is_published'] as boolean) ?? true,
    displayOrder: (row['display_order'] as number) ?? 0,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
